import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginAction } from '../../action_creators/app_load_action_creators';
import AuthHeader from './auth_header';
import { linkWithQueryParams } from '../../services/urls';
import { isEmbeddedInSmorgCompanion } from '../../services/smorg_companion_auth';
import {
  END_USER_SIGNUP_ROUTE,
  SIGNUP_SMORG_COMPANION_ROUTE,
} from '../../services/routes';
import DarkBackgroundTextField from './darkbackground_textfield';
import { spaceIDFromAppUrl } from '../../services/spaces';
import { useGetPublicSpaceQuery } from '../../app/smorgAppSyncGraphQLApi';
import ThemeChangeListener from '../common/theme_change_listener';
import { SPACE_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import { setEndUserDesiredSpaceID } from '../../features/white_label/white_label_slice';

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState('');
  const [cleartextPassword, setCleartextPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const intoSpaceID = spaceIDFromAppUrl();
  const membershipTierID = searchParams.get('chosenMembershipTier');

  const isRunningInsideSmorgCompanion = isEmbeddedInSmorgCompanion();

  const { data: space } = useGetPublicSpaceQuery(intoSpaceID, {
    skip: !intoSpaceID,
    refetchOnMountOrArgChange: SPACE_REFRESH_INTERVAL_SECONDS,
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const spaceBackgroundColor =
    space?.whiteLabelElements?.backgroundColor || '#427A46';

  const customTermsUrl = space?.whiteLabelElements?.termsUrl;

  const customPrivacyPolicyUrl = space?.whiteLabelElements?.privacyPolicyUrl;

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setShowPassword(false);
      await dispatch(loginAction(email, cleartextPassword));
      if (returnTo) {
        console.log(`Navigating to ${returnTo}`);
        window.location.href = returnTo;
      } else {
        console.log(`Navigating to home page`);
        navigate(
          linkWithQueryParams('/', {
            chosenMembershipTier: membershipTierID,
          }),
        );
      }
    } catch (err) {
      console.log({ err });
      setErrorMessage('Login failed, please check your email and/or password');
    } finally {
      setIsLoading(false);
    }
  };

  const resetErrorMessage = () => {
    setErrorMessage('');
  };
  const getLink = () => {
    if (isRunningInsideSmorgCompanion) {
      return (
        <DialogContentText sx={{ backgroundColor: spaceBackgroundColor }}>
          <div>
            Not yet subscribed?{' '}
            <Link
              to={linkWithQueryParams(SIGNUP_SMORG_COMPANION_ROUTE, {
                returnTo,
                intoSpace: intoSpaceID,
                chosenMembershipTier: membershipTierID,
              })}>
              Subscribe here
            </Link>
          </div>
        </DialogContentText>
      );
    }
    return (
      <DialogContentText sx={{ backgroundColor: spaceBackgroundColor }}>
        <div>
          Not yet subscribed?{' '}
          <Link
            to={linkWithQueryParams(END_USER_SIGNUP_ROUTE, {
              returnTo,
              intoSpace: intoSpaceID,
              chosenMembershipTier: membershipTierID,
            })}>
            Create an account
          </Link>
        </div>
      </DialogContentText>
    );
  };

  useEffect(() => {
    if (intoSpaceID) {
      dispatch(setEndUserDesiredSpaceID(intoSpaceID));
    }
  }, [dispatch, intoSpaceID]);

  return (
    <main className="authForm">
      <AuthHeader />
      <ThemeChangeListener />
      <Dialog
        open
        maxWidth="sm"
        className="authDialog darkBackgroundAuthForm"
        fullWidth
        BackdropProps={{ invisible: true }}>
        <DialogTitle
          sx={{ backgroundColor: spaceBackgroundColor, paddingBottom: '30px' }}>
          Login
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: spaceBackgroundColor }}>
          <DialogContentText>Email address</DialogContentText>
          <DarkBackgroundTextField
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              resetErrorMessage();
            }}
            autoFocus
            margin="dense"
            type="email"
            fullWidth
            size="small"
            variant="outlined"
            style={{ margin: '10px 0' }}
          />
          <DialogContentText>Password</DialogContentText>
          <DarkBackgroundTextField
            value={cleartextPassword}
            onChange={(ev) => {
              setCleartextPassword(ev.target.value);
              resetErrorMessage();
            }}
            margin="dense"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            size="small"
            variant="outlined"
            style={{ margin: '10px 0' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    tabIndex={-1}
                    edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errorMessage && (
            <div
              style={{
                paddingTop: '5px',
                paddingBottom: '5px',
                color: 'red',
                fontSize: '14px',
              }}>
              {errorMessage}
            </div>
          )}
          <DialogContentText>
            Forgot your password?{' '}
            <Link
              to={linkWithQueryParams('/forgotPassword', {
                returnTo,
                intoSpace: intoSpaceID,
              })}>
              Click here to reset it
            </Link>
          </DialogContentText>
          <DialogContentText>
            <br />
            By continuing, you agree to the{' '}
            <a
              href={customTermsUrl || 'https://www.smorg.io/terms'}
              target="_new">
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a
              href={customPrivacyPolicyUrl || 'https://www.smorg.io/privacy'}
              target="_new">
              Privacy Policy
            </a>
            .
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'flex-start',
            paddingBottom: '50px',
            backgroundColor: spaceBackgroundColor,
          }}>
          <Box
            width="100%"
            sx={{
              padding: '0 20px',
              display: 'flex',
              flexDirection: 'column',
            }}>
            {getLink()}
            <Box display="flex" style={{ marginTop: '20px' }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleLogin}
                disabled={isLoading}
                style={{
                  borderRadius: '30px',
                  margin: '10px 0 23px',
                  flexGrow: 0,
                  flexShrink: 0,
                }}>
                Log in
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default LoginForm;
